import { createSelector } from 'reselect'
import type { State } from 'types/redux'
import { PLAN_ACTIVE, IS_GBSTORAGE } from '@disco/constants/FeatureTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  ARTIST_PLANS_LIST,
  BUSINESS_PLANS_LIST,
} from '@disco/state-management/reducers/forms/pricing'
import type { SubscriptionChargebeeData } from 'types/subscription'
import { getPrePopulatedSubscriptionData } from '@disco/state-management/selectors/user'
import { getPlans } from '@disco/state-management/selectors/pricing'

/**
 * Gets the subscription billing period from Chargebee data.
 * @param state - The Redux state
 * @returns The billing period ('annual' or 'monthly'), or null if not available
 * @todo Update API response yearly->annual - then remove this adapter
 */
export const getSubscriptionPeriod = (
  state: State,
): ('annual' | 'monthly') | null | undefined => {
  const subscription = getSubscription(state)

  if (!subscription?.loaded) return null

  const interval = subscription.subscription_interval
  if (interval === 'yearly') return 'annual' // TODO: update API response yearly->annual - then remove this adapter
  // TODO:: add support for 6-months, 2-year, 3-year subscriptions, currently API returns `null` for them, because we need to support them in UI first

  return interval
}

/**
 * Checks if tax calculation is needed for the subscription.
 * @param state - The Redux state
 * @returns True if tax needs to be calculated (US admin subscriber without address)
 */
export const isTaxNotCalculated = (state: State): boolean => {
  const { user } = state
  const subscription = state.subscription?.chargebee
  const adminCondition = user.is_admin
  const regionCondition = user.business?.region?.toLowerCase()?.includes('us')
  const contactCondition =
    subscription && !(subscription.address && subscription.zip)
  const subscriber =
    user?.business?.private_metadata?.plan_type === 'subscriber'
  return Boolean(
    subscriber && adminCondition && regionCondition && contactCondition,
  )
}

/**
 * Checks if any plans from a list are available for the business.
 * @param state - The Redux state
 * @param plansList - Array of plan handles to check
 * @returns True if any plans from the list are available
 */
const isPlansAvailable = (state: State, plansList): boolean => {
  // if business doesn't contain pre_populated_subscription_data then all plans are available
  if (!getPrePopulatedSubscriptionData(state)?.available_plans) return true
  const availablePlans = getPlans(state)
  return plansList.some(planHandle =>
    availablePlans.find(({ handle }) => handle === planHandle),
  )
}

/**
 * Checks if business plans are available for the current user.
 * @param state - The Redux state
 * @returns True if business plans are available
 */
export const isBusinessPlansAvailable = (state: State): boolean => {
  return isPlansAvailable(state, BUSINESS_PLANS_LIST)
}
/**
 * Checks if artist plans are available for the current user.
 * @param state - The Redux state
 * @returns True if artist plans are available
 */
export const isArtistPlansAvailable = (state: State): boolean => {
  return isPlansAvailable(state, ARTIST_PLANS_LIST)
}

export const getSubscription = (
  state: State,
): SubscriptionChargebeeData['chargebee'] => {
  const { subscription } = state
  if (subscription.chargebee) {
    return { ...subscription.chargebee, loaded: true, provider: 'chargebee' }
  }

  if (!subscription.loaded)
    return {
      loaded: false,
      // @ts-expect-error Type '"invalid"' is not assignable to type '"chargebee"'.
      provider: 'invalid',
      card_type: '',
      current_billing_amount_in_cents: 0,
      expires_at: '',
      first_name: '',
      last_name: '',
      last_segment_of_card_number: '',
      subscription_interval: 'monthly',
      subscription_id: '',
      address: '',
      address_2: '',
      zip: '',
      city: '',
      state: '',
      country: '',
      email: '',
      vat_number: '',
      scheduled_cancellation_at: null,
      pricing_variant: '2025-pricing',
    }
}

export const getSubscriptionPricingVariant = createSelector(
  [getSubscription],
  (subscription): string => {
    return subscription?.pricing_variant ?? ''
  },
)

export const getSubscriptionCurrentAmount = createSelector(
  [getSubscription],
  (subscription): string | null => {
    return subscription
      ? `$${(
          subscription?.current_billing_amount_in_cents * 0.01
        ).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : null
  },
)

/**
 * Gets the subscription expiration date from Chargebee data.
 * @param state - The Redux state
 * @returns The expiration date, or null if loading or not available
 */
export const getSubscriptionExpiresAt = (state: State): Date | null => {
  if (state.subscription.loading) {
    return null
  }

  return state.subscription?.chargebee?.expires_at || null
}

/**
 * Gets the scheduled cancellation date from Chargebee data.
 * @param state - The Redux state
 * @returns The scheduled cancellation date, or null if loading or not available
 */
export const getSubscriptionScheduledCancellationAt = (
  state: State,
): Date | null => {
  if (state.subscription.loading) {
    return null
  }

  return state.subscription?.chargebee?.scheduled_cancellation_at || null
}

/**
 * Whether the business's subscription has expired. Drives the route guard that
 * redirects to the expired-subscription page.
 * @param state - The Redux state
 * @returns true if the subscription is expired
 */
export const getIsExpiredSubscription = (state: State): boolean =>
  Boolean(state.user?.business?.private_metadata?.expired_subscription)

/**
 * Checks if the subscription data is currently loading.
 * @param state - The Redux state
 * @returns True if subscription is loading, false otherwise
 */
export const getSubscriptionLoading = (state: State): boolean => {
  return state.subscription.loading
}

export const getSelfServeBillingPermissions = (
  state: State,
): {
  no_seats_and_tracks_due_to: string // empty string = allowed
} => {
  const subscriptionPeriod = getSubscriptionPeriod(state)
  const isGbstorage = isFeatureEnabled(state, { featureName: IS_GBSTORAGE })
  const planActive = isFeatureEnabled(state, {
    featureName: PLAN_ACTIVE,
  })
  const isBankDraftSubscriber =
    state.user?.business?.private_metadata?.plan_type ===
    'bank_draft_subscriber'

  let noComponentPurchaseDueTo = ''
  if (!planActive) noComponentPurchaseDueTo += ' not_plan_active'
  if (isGbstorage) noComponentPurchaseDueTo += ' supervisor_plan' // TODO: replace IS_GBSTORAGE with SELF_SERVE_COMPONENTS_CHANGE
  if (!subscriptionPeriod) noComponentPurchaseDueTo += ' unsupported_period' // TODO: enable billing on 2-year subscriptions
  if (isBankDraftSubscriber) noComponentPurchaseDueTo += ' bank_draft'
  return {
    no_seats_and_tracks_due_to: noComponentPurchaseDueTo, // UI-level interpretation
  }
}
